import { formatCurrency, formatPercent } from '../../lib/api';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export const KPICard = ({ 
  label, 
  value, 
  format = 'currency', 
  trend = null, 
  trendValue = null,
  variant = 'default',
  onClick = null
}) => {
  const formattedValue = format === 'currency' 
    ? formatCurrency(value || 0)
    : format === 'percent'
    ? formatPercent(value || 0)
    : value;
    
  const getTrendIcon = () => {
    if (trend === 'up') return <TrendingUp size={14} className="text-lemas-green" />;
    if (trend === 'down') return <TrendingDown size={14} className="text-lemas-danger" />;
    return <Minus size={14} className="text-[rgb(var(--lm-text-secondary))]" />;
  };
  
  const variantClasses = {
    default: 'border-[rgb(var(--lm-border))]',
    gold: 'border-lemas-gold/30 card-glow-gold',
    green: 'border-lemas-green/30 card-glow-green',
    danger: 'border-lemas-danger/30',
    warning: 'border-lemas-warning/30',
  };
  
  return (
    <div 
      className={`kpi-card ${variantClasses[variant]} ${onClick ? 'cursor-pointer transition-lift hover:border-white/20' : ''}`}
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <p className="kpi-label mb-2">{label}</p>
      <p className="kpi-value text-[rgb(var(--lm-text))]">{formattedValue}</p>
      {(trend || trendValue !== null) && (
        <div className="flex items-center gap-1 mt-2">
          {getTrendIcon()}
          {trendValue !== null && (
            <span className={`text-sm font-mono ${
              trend === 'up' ? 'text-lemas-green' : 
              trend === 'down' ? 'text-lemas-danger' : 
              'text-[rgb(var(--lm-text-secondary))]'
            }`}>
              {trendValue > 0 ? '+' : ''}{formatPercent(trendValue)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const AlertCard = ({ type = 'warning', title, message }) => {
  const typeClasses = {
    danger: 'alert-danger',
    warning: 'alert-warning',
    success: 'alert-success',
  };
  
  return (
    <div className={`alert-card ${typeClasses[type]}`} data-testid={`alert-${type}`}>
      <p className="font-medium font-body">{title}</p>
      <p className="text-sm mt-1 opacity-80">{message}</p>
    </div>
  );
};

export const Badge = ({ type = 'default', children }) => {
  const typeClasses = {
    rupture: 'badge-rupture',
    nouveau: 'badge-nouveau',
    success: 'badge-success',
    default: 'bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text))] border border-white/20',
  };
  
  return (
    <span className={`text-xs px-2 py-1 rounded uppercase tracking-wider font-body ${typeClasses[type]}`}>
      {children}
    </span>
  );
};

export const ProgressBar = ({ value, max, variant = 'gold', showLabel = true }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  
  const variantClasses = {
    gold: 'bg-lemas-gold',
    green: 'bg-lemas-green',
    danger: 'bg-lemas-danger',
    warning: 'bg-lemas-warning',
  };
  
  // Determine color based on percentage
  let barColor = variantClasses[variant];
  if (percentage >= 85) barColor = 'bg-lemas-danger';
  else if (percentage >= 60) barColor = 'bg-lemas-warning';
  
  return (
    <div className="w-full">
      <div className="progress-bg h-3 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-500 rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {showLabel && (
        <p className="text-sm text-[rgb(var(--lm-text-secondary))] mt-1 font-mono">{percentage.toFixed(0)}%</p>
      )}
    </div>
  );
};

export const DataTable = ({ columns, data, emptyMessage = "Aucune donnée" }) => {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[rgb(var(--lm-border))]">
            {columns.map((col, i) => (
              <th 
                key={i} 
                className="text-left py-3 px-4 text-sm uppercase tracking-wider text-[rgb(var(--lm-text-secondary))] font-body font-medium"
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="py-8 text-center text-[rgb(var(--lm-text-muted))]">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-b border-[rgb(var(--lm-border))] hover:bg-[rgb(var(--lm-surface-elevated))] transition-colors">
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="py-3 px-4 font-body">
                    {col.render ? col.render(row) : row[col.accessor]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
